/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {NotificationFilters} from '@ion/dls/src/filters/NotificationFilters';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import Hidden from '@ion/dls/src/layout/Hidden';
import {
  NotificationDrawer, NotificationTabType
} from '@ion/dls/src/notifications/NotificationDrawer';
import {
  UserNotificationDialog
} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {NotificationPanelTabs} from '@ion/dls/src/types/types';
import {NotificationMenu} from '../notifications/NotificationMenu';
import NotificationPanelContent from '../notifications/NotificationPanelContent';
import {exportNotificationPanel} from '../store/actions/exporter';
import {
  deselectAllNotifications,
  markAllAsCleared,
  markAllAsRead,
  markAsRead,
  resetNotifications,
  selectAllNotifications,
  setNotificationsActivePanel,
  setNotificationsPanelStatus,
  showNotificationFilters
} from './state';

export interface NotificationPanelType {
  cancelToastDismiss: any[];
  setCancelToastDismiss: Function;
  omittedMenuItems?: GenericObject;
  omitUrgentTab?: boolean;
}

type ClearScope = 'all' | 'selected';

const clearScopeCopy: Record<ClearScope, {title: string, desktop: string, mobile: string}> = {
  all : {
    title   : 'notifications:dialog.clearAll',
    desktop : 'notifications:dialog.clearAllConfirmation',
    mobile  : 'notifications:dialog.allConfirmation'
  },
  selected : {
    title   : 'notifications:dialog.clearSelected',
    desktop : 'notifications:dialog.clearConfirmation',
    mobile  : 'notifications:dialog.confirmation'
  }
};

// defines the styles used by Notifiction ...
const useStyles = makeStyles(theme => ({
  bottomNotification : {
    width          : '100%',
    height         : '60px',
    display        : 'flex',
    bottom         : 0,
    position       : 'absolute',
    alignItems     : 'center',
    justifyContent : 'center',
    background     : dlsColor('grey6'),
    boxShadow      : `0px 2px 10px ${dlsColor('grey3')}`
  },
  preferencesTitle : {
    color : dlsColor('grey1')
  }
}));

export const NotificationPanel = ({
  cancelToastDismiss,
  setCancelToastDismiss,
  omittedMenuItems,
  omitUrgentTab = false
}: NotificationPanelType) => {
  const dispatch = useDispatch();
  const classes  = useStyles();
  const notificationPanelOpen       = useSelector(({notificationsPanel: {panel}}) => panel.open);
  const activeNotificationTab       = useSelector(({notificationsPanel: {panel}}) => panel.activePanel);
  const selectedNotifications       = useSelector(({notificationsPanel: {panel}}) => panel.selected);
  const allNotifications            = useSelector(({notificationsPanel}) => notificationsPanel.all);
  const urgentNotifications         = useSelector(({notificationsPanel}) => notificationsPanel.urgent);
  const loading                     = useSelector(({notificationsPanel: {loading}}) => loading);
  const isFilter                    = useSelector(({notificationsPanel: {isFilter}}) => isFilter);
  const {selectedFilter}            = useSelector(({notificationsPanel}) => notificationsPanel);
  const {globalGroup}               = useSelector(({globalGroup}) => globalGroup);
  const [checkboxesEnabled, setCheckboxesEnabled]         = useState(false);
  const [filterUnread, setFilterUnread]                   = useState(false);
  const [clearAllVerification, setClearAllVerification]   = useState(false);
  const [clearScope, setClearScope]                       = useState<ClearScope>('selected');
  const [notificationMenuOpen, setNotificationMenuOpen]   = useState(false);
  const [notificationsCheckAll, setNotificationsCheckAll] = useState(false);
  const [customFilter, setCustomFilter]                   = useState(false);
  const [isSubAccount, setIsSubAccount]                   = useState(false);
  const [notificationRows, setNotificationRows]           = useState('100');
  const {isDesktop, isPhone}                              = useMedia();
  const isMounted                                         = useHasMounted();

  const tabItems: NotificationTabType[] = [
    {
      tab       : NotificationPanelTabs.urgent,
      tabHeader : translate('notifications:panel.panelTabs.urgent')
    },
    {
      tab       : NotificationPanelTabs.allActivity,
      tabHeader : translate('notifications:panel.panelTabs.allActivity')
    }
  ];

  useEffect(() => {
    // If urgent notifications have been loaded and have no length then the
    // tab would automatically be pushed to All Activity
    // *Note this would also happen should a user delete their last urgent
    // notification.
    if (isMounted) {
      const urgentNotificationsLoaded = Array.isArray(urgentNotifications);
      if (omitUrgentTab || (!urgentNotifications?.length && urgentNotificationsLoaded)) {
        onNotificationTabChange(tabItems[1]);
      } else if (!activeNotificationTab) {
        onNotificationTabChange(tabItems[0]);
      }
    }
  }, [urgentNotifications]);

  function closeNotifications() {
    dispatch(setNotificationsPanelStatus(false));
  }

  function onNotificationTabChange(tab: NotificationTabType) {
    dispatch(setNotificationsActivePanel(tab.tab));
  }

  function onReviewNotification(notification) {
    setCancelToastDismiss([...cancelToastDismiss, notification?.uuid]);
    isPhone && dispatch(setNotificationsPanelStatus(false));
    dispatch(markAsRead(notification));
  }

  function onSelectMultiple() {
    setCheckboxesEnabled(!checkboxesEnabled);
    setNotificationMenuOpen(false);
  }

  function onMarkAllAsRead() {
    dispatch(markAllAsRead(allNotifications));
    setNotificationMenuOpen(false);
  }

  function onSeeUnreadOnly() {
    setFilterUnread(!filterUnread);
    setNotificationMenuOpen(false);
  }

  function onMarkSelectedAsRead() {
    dispatch(markAllAsRead(selectedNotifications));
    setCheckboxesEnabled(false);
    setNotificationMenuOpen(false);
  }

  function onClearSelected() {
    setClearScope('selected');
    setClearAllVerification(true);
  }

  function onClearAll() {
    setClearScope('all');
    setClearAllVerification(true);
    setNotificationMenuOpen(false);
  }

  /**
   * Provides the function to export the notifications
   */
  const onExport = function() {
    dispatch(exportNotificationPanel({
      columns : translate('notifications:columns', {returnObjects: true}),
      items   : []
    }));
    setNotificationMenuOpen(false);
  };

  /**
   * Provides the function to Filter the notifications
   */
  const onFilter = function() {
    setCustomFilter(!customFilter);
    dispatch(showNotificationFilters());
    setNotificationMenuOpen(false);
  };

  /**
     * Provides the function to reset the notifications
     */
  const onReset = function() {
    setCustomFilter(false);
    dispatch(resetNotifications());
    setNotificationMenuOpen(false);
  };

  /**
     * Provides the function to Include Sub accounts the notifications
     */
  const onSubAccount = function() {
    dispatch(resetNotifications({isSubAccount: !isSubAccount}));
    setIsSubAccount(!isSubAccount);
  };

  function clearAllSelectedNotifications() {
    dispatch(markAllAsCleared(clearScope === 'all' ? allNotifications : selectedNotifications));
    setCheckboxesEnabled(false);
    setClearAllVerification(false);
    setNotificationMenuOpen(false);
  }

  function handleNotificationSelectAll(e) {
    e.stopPropagation();
    if (!notificationsCheckAll) {
      dispatch(selectAllNotifications());
    } else {
      dispatch(deselectAllNotifications());
    }
    setNotificationsCheckAll(!notificationsCheckAll);
  }

  /**
   * Invoked when the user selects a specific Notification rows count.
   */
  const onSetNotificationRows = function(value) {
    setNotificationRows(value);
    dispatch(resetNotifications({pageSize: value}));
  };

  return (
    <>
      <Hidden show='desktop'>
        <NotificationMenu checkboxesEnabled={checkboxesEnabled}
          omittedMenuItems={omittedMenuItems}
          onSelectMultiple={onSelectMultiple}
          onMarkAllAsRead={onMarkAllAsRead}
          onSeeUnreadOnly={onSeeUnreadOnly}
          onMarkSelectedAsRead={onMarkSelectedAsRead}
          onClearSelected={onClearSelected}
          onClearAll={onClearAll}
          onExport={onExport}
          onFilter={onFilter}
          onReset={onReset}
          onSubAccount={onSubAccount}
          isSubAccount={isSubAccount}
          filterUnread={filterUnread}
          customFilter={customFilter}>
          <NotificationDrawer panelOpen={notificationPanelOpen}
            closeDrawer={closeNotifications}
            onTabChange={onNotificationTabChange}
            tabItems={tabItems}
            hideTabs={omitUrgentTab}
            activeTab={activeNotificationTab}
            notificationMenuOpen={notificationMenuOpen}
            setNotificationMenuOpen={setNotificationMenuOpen}
            checkboxesEnabled={checkboxesEnabled}
            handleSelectAll={handleNotificationSelectAll}
            notificationsCheckAll={notificationsCheckAll}
            onClearSelected={onClearSelected}
            selectedNotifications={selectedNotifications}>
            <NotificationPanelContent checkboxesEnabled={checkboxesEnabled}
              filterUnread={filterUnread}
              onReviewClick={onReviewNotification} />
          </NotificationDrawer>
        </NotificationMenu>
      </Hidden>
      <Hidden show='mobile'>
        <NotificationMenu checkboxesEnabled={checkboxesEnabled}
          omittedMenuItems={omittedMenuItems}
          onSelectMultiple={onSelectMultiple}
          onMarkAllAsRead={onMarkAllAsRead}
          onSeeUnreadOnly={onSeeUnreadOnly}
          onMarkSelectedAsRead={onMarkSelectedAsRead}
          onClearSelected={onClearSelected}
          onClearAll={onClearAll}
          onExport={onExport}
          onFilter={onFilter}
          onSubAccount={onSubAccount}
          isSubAccount={isSubAccount}
          filterUnread={filterUnread}
          customFilter={customFilter}>
          <NotificationDrawer panelOpen={notificationPanelOpen}
            closeDrawer={closeNotifications}
            onTabChange={onNotificationTabChange}
            tabItems={tabItems}
            hideTabs={omitUrgentTab}
            activeTab={activeNotificationTab}
            notificationMenuOpen={notificationMenuOpen}
            setNotificationMenuOpen={setNotificationMenuOpen}
            checkboxesEnabled={checkboxesEnabled}
            handleSelectAll={handleNotificationSelectAll}
            notificationsCheckAll={notificationsCheckAll}>
            <NotificationPanelContent checkboxesEnabled={checkboxesEnabled}
              filterUnread={filterUnread}
              onReviewClick={onReviewNotification} />
          </NotificationDrawer>
        </NotificationMenu>
      </Hidden>
      {isFilter && <NotificationFilters open
        selectedFilter={selectedFilter}
        account={globalGroup}
        loading={loading}
        onSubmit={function(params) {
          dispatch(resetNotifications(params));
        }}
        onCancel={function() {
          dispatch(showNotificationFilters(false));
        }}/>
      }
      <UserNotificationDialog show={clearAllVerification}
        title={translate(clearScopeCopy[clearScope].title)}
        message={isDesktop ? translate(clearScopeCopy[clearScope].desktop)
          : translate(clearScopeCopy[clearScope].mobile)}
        actionLabel={translate('notifications:dialog.yesClear')}
        onAction={clearAllSelectedNotifications}
        dismissLabel={translate('notifications:dialog.neverMind')}
        onDismiss={function() {
          setClearAllVerification(false);
        }}
        capitalize />
    </>
  );
};

export default NotificationPanel;
